import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMyMedicalCheckIns } from '../../../api';
import MedicalHistoryTable from './MedicalHistoryTable';
import MedicalCheckInModal from './MedicalCheckInModal';
import {
  PAGE_SIZE,
  STATUS_OPTIONS,
  SYMPTOM_OPTIONS,
  TIME_RANGES,
  applyFilters,
  getPageWindow,
  sortByDate,
  summarize,
} from '../../../utils/medicalHistory';

const NO_FILTERS = { time: 'all', symptom: 'all', status: 'all' };

const PAGER_BUTTON =
  'h-11 min-w-[2.75rem] rounded-xl border border-gray-200 bg-white px-3 text-base font-extrabold text-slate-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';

function StatCard({ icon, value, label, tone }) {
  return (
    <div className={`flex items-center gap-4 rounded-2xl px-5 py-4 ${tone}`}>
      <span className="text-4xl leading-none" aria-hidden="true">
        {icon}
      </span>
      <div>
        <p className="text-3xl font-extrabold leading-none text-slate-900">{value}</p>
        <p className="mt-1 text-sm font-semibold text-slate-600">{label}</p>
      </div>
    </div>
  );
}

function FilterSelect({ icon, label, value, onChange, options }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg" aria-hidden="true">
        {icon}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="w-full appearance-none rounded-xl border border-gray-200 bg-white py-3 pl-12 pr-10 text-[15px] font-semibold text-slate-700 shadow-sm"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500" aria-hidden="true">
        ▼
      </span>
    </div>
  );
}

/**
 * Medical: the student's own check-ins. Loads them once, then filters, sorts and
 * pages them in the browser (a student only has a handful). Read-only - the
 * coaches record and update check-ins from their own dashboard.
 */
export default function MedicalHistoryView() {
  const [records, setRecords] = useState(null); // null while loading
  const [failed, setFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const openerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setRecords(null);
    setFailed(false);
    getMyMedicalCheckIns()
      .then((response) => {
        if (!cancelled) setRecords(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadCount]);

  const stats = useMemo(() => summarize(records || []), [records]);
  const visible = useMemo(
    () => sortByDate(applyFilters(records || [], filters), sortDirection),
    [records, filters, sortDirection]
  );

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const firstIndex = (currentPage - 1) * PAGE_SIZE;
  const pageRows = visible.slice(firstIndex, firstIndex + PAGE_SIZE);
  const isFiltered = filters.time !== 'all' || filters.symptom !== 'all' || filters.status !== 'all';

  const changeFilter = (name) => (value) => {
    setFilters((previous) => ({ ...previous, [name]: value }));
    setPage(1);
  };

  const openDetails = (record, opener) => {
    openerRef.current = opener;
    setSelected(record);
  };
  const closeDetails = useCallback(() => {
    setSelected(null);
    openerRef.current?.focus?.();
  }, []);

  let body;
  if (failed) {
    body = (
      <div role="alert" className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
        <p className="mb-3 font-bold text-red-700">We couldn't load your medical history.</p>
        <button
          type="button"
          onClick={() => setReloadCount((count) => count + 1)}
          className="rounded-xl bg-red-600 px-5 py-2.5 font-extrabold text-white hover:bg-red-700"
        >
          Try again
        </button>
      </div>
    );
  } else if (records === null) {
    body = (
      <p role="status" className="rounded-2xl bg-white/70 p-6 text-center font-bold text-gray-500">
        Loading your medical history...
      </p>
    );
  } else if (records.length === 0) {
    body = (
      <div className="rounded-2xl border border-gray-100 bg-white p-10 text-center shadow-sm">
        <p className="mb-2 text-5xl" aria-hidden="true">
          🌟
        </p>
        <p className="text-xl font-extrabold text-slate-900">No check-ups yet</p>
        <p className="font-medium text-gray-500">When you see the doctor or your coach checks on you, it will show up here.</p>
      </div>
    );
  } else {
    body = (
      <>
        <div className="mb-4 grid grid-cols-1 gap-4 [@container_(min-width:640px)]:grid-cols-3">
          <StatCard icon="🩺" value={stats.total} label="Total Check-ups" tone="bg-rose-50" />
          <StatCard icon="💊" value={stats.treatments} label="Treatments" tone="bg-green-50" />
          <StatCard icon="🧪" value={stats.tests} label="Tests Done" tone="bg-violet-50" />
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 [@container_(min-width:640px)]:grid-cols-3">
          <FilterSelect icon="📅" label="Filter by time" value={filters.time} onChange={changeFilter('time')} options={TIME_RANGES} />
          <FilterSelect
            icon="🩺"
            label="Filter by symptom"
            value={filters.symptom}
            onChange={changeFilter('symptom')}
            options={[{ value: 'all', label: 'All Symptoms' }, ...SYMPTOM_OPTIONS]}
          />
          <FilterSelect
            icon="💊"
            label="Filter by status"
            value={filters.status}
            onChange={changeFilter('status')}
            options={[{ value: 'all', label: 'All Status' }, ...STATUS_OPTIONS]}
          />
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          {visible.length === 0 ? (
            <div className="p-10 text-center">
              <p className="mb-3 font-bold text-gray-600">No check-ups match these filters.</p>
              <button
                type="button"
                onClick={() => {
                  setFilters(NO_FILTERS);
                  setPage(1);
                }}
                className="rounded-xl bg-blue-600 px-5 py-2.5 font-extrabold text-white hover:bg-blue-700"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <MedicalHistoryTable
              rows={pageRows}
              sortDirection={sortDirection}
              onToggleSort={() => setSortDirection((direction) => (direction === 'desc' ? 'asc' : 'desc'))}
              onView={openDetails}
            />
          )}

          {visible.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
              <p className="text-[15px] font-bold text-blue-700">
                Showing {firstIndex + 1} to {firstIndex + pageRows.length} of {visible.length} {isFiltered ? 'matching ' : ''}
                records
              </p>
              {pageCount > 1 && (
                <nav aria-label="Pages" className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                    className={PAGER_BUTTON}
                  >
                    ‹
                  </button>
                  {getPageWindow(currentPage, pageCount).map((entry, index) =>
                    entry === 'gap' ? (
                      <span key={`gap-${index}`} aria-hidden="true" className="px-1 font-extrabold text-gray-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={entry}
                        type="button"
                        onClick={() => setPage(entry)}
                        aria-label={`Page ${entry}`}
                        aria-current={entry === currentPage ? 'page' : undefined}
                        className={`${PAGER_BUTTON} ${
                          entry === currentPage ? '!border-blue-600 !bg-blue-600 text-white hover:!bg-blue-600' : ''
                        }`}
                      >
                        {entry}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage === pageCount}
                    aria-label="Next page"
                    className={PAGER_BUTTON}
                  >
                    ›
                  </button>
                </nav>
              )}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6 flex items-center gap-4">
        <span
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-red-500 shadow-sm"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" className="h-8 w-8" fill="#fff">
            <path d="M9 3h6v6h6v6h-6v6H9v-6H3V9h6z" />
          </svg>
        </span>
        <div>
          <h1 className="text-[28px] font-extrabold leading-tight text-slate-900">My Medical History</h1>
          <p className="text-[15px] font-medium text-gray-500">I stay healthy, I can do more!</p>
        </div>
      </div>

      {body}

      {selected && <MedicalCheckInModal record={selected} onClose={closeDetails} />}
    </>
  );
}
